import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Zap, Users, MessageSquare, TrendingUp, CheckCircle, XCircle, 
  Clock, Play, Pause, Eye, Trash2, Edit2, RefreshCw, Search,
  DollarSign, Percent, AlertCircle, Link2, CreditCard, Receipt
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const getAdminHeaders = () => {
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  return { 'X-Admin-Id': adminUser?.id };
};

// Translations
const TEXTS = {
  es: {
    title: 'Gestión de Reactivación IA',
    subtitle: 'Administra campañas, demos y facturación',
    copyDemoLink: 'Copiar Link Demo',
    campaigns: 'Campañas',
    active: 'Activas',
    contacts: 'Contactos',
    converted: 'Convertidos',
    revenue: 'Ingresos',
    commission: 'Comisión',
    demoLinkTitle: 'Link de Demo para Negocios',
    demoLinkDesc: 'Comparte este enlace durante videollamadas para que los dueños prueben el agente',
    allStatuses: 'Todos los estados',
    inReview: 'En Revisión',
    approved: 'Aprobadas',
    completed: 'Completadas',
    rejected: 'Rechazadas',
    noCampaigns: 'No hay campañas',
    campaignsWillAppear: 'Las campañas aparecerán aquí cuando los negocios las creen',
    business: 'Negocio',
    created: 'Creada',
    responses: 'Respuestas',
    setupFee: 'Setup Fee',
    commissionMin: 'Comisión (%) - Mín. 20%',
    status: 'Estado',
    adminNotes: 'Notas Admin',
    internalNotes: 'Notas internas...',
    cancel: 'Cancelar',
    save: 'Guardar',
    configure: 'Configurar',
    start: 'Iniciar',
    approve: 'Aprobar',
    reject: 'Rechazar',
    offer: 'Oferta',
    recentDemos: 'Demos Recientes',
    landingLinked: 'Landing Page Vinculada',
    visits: 'Visitas',
    conversions: 'Conversiones',
    appointments: 'Citas',
    view: 'Ver',
    pendingPayments: 'Pagos Pendientes',
    markAsPaid: 'Marcar como Pagado',
    paymentReceived: 'Pago Recibido',
    pendingCommissions: 'Comisiones Pendientes',
    collectCommission: 'Cobrar',
    totalPending: 'Total Pendiente',
    setupPaid: 'Setup Pagado',
    setupPending: 'Setup Pendiente'
  },
  en: {
    title: 'AI Reactivation Management',
    subtitle: 'Manage campaigns, demos and billing',
    copyDemoLink: 'Copy Demo Link',
    campaigns: 'Campaigns',
    active: 'Active',
    contacts: 'Contacts',
    converted: 'Converted',
    revenue: 'Revenue',
    commission: 'Commission',
    demoLinkTitle: 'Demo Link for Businesses',
    demoLinkDesc: 'Share this link during video calls so owners can try the agent',
    allStatuses: 'All statuses',
    inReview: 'In Review',
    approved: 'Approved',
    completed: 'Completed',
    rejected: 'Rejected',
    noCampaigns: 'No campaigns',
    campaignsWillAppear: 'Campaigns will appear here when businesses create them',
    business: 'Business',
    created: 'Created',
    responses: 'Responses',
    setupFee: 'Setup Fee',
    commissionMin: 'Commission (%) - Min. 20%',
    status: 'Status',
    adminNotes: 'Admin Notes',
    internalNotes: 'Internal notes...',
    cancel: 'Cancel',
    save: 'Save',
    configure: 'Configure',
    start: 'Start',
    approve: 'Approve',
    reject: 'Reject',
    offer: 'Offer',
    recentDemos: 'Recent Demos',
    landingLinked: 'Linked Landing Page',
    visits: 'Visits',
    conversions: 'Conversions',
    appointments: 'Appointments',
    view: 'View',
    pendingPayments: 'Pending Payments',
    markAsPaid: 'Mark as Paid',
    paymentReceived: 'Payment Received',
    pendingCommissions: 'Pending Commissions',
    collectCommission: 'Collect',
    totalPending: 'Total Pending',
    setupPaid: 'Setup Paid',
    setupPending: 'Setup Pending'
  },
  fr: {
    title: 'Gestion de Réactivation IA',
    subtitle: 'Gérez les campagnes, démos et facturation',
    copyDemoLink: 'Copier le Lien Démo',
    campaigns: 'Campagnes',
    active: 'Actives',
    contacts: 'Contacts',
    converted: 'Convertis',
    revenue: 'Revenus',
    commission: 'Commission',
    demoLinkTitle: 'Lien de Démo pour les Entreprises',
    demoLinkDesc: 'Partagez ce lien lors des appels vidéo pour que les propriétaires testent l\'agent',
    allStatuses: 'Tous les statuts',
    inReview: 'En Révision',
    approved: 'Approuvées',
    completed: 'Terminées',
    rejected: 'Rejetées',
    noCampaigns: 'Pas de campagnes',
    campaignsWillAppear: 'Les campagnes apparaîtront ici lorsque les entreprises les créeront',
    business: 'Entreprise',
    created: 'Créée',
    responses: 'Réponses',
    setupFee: 'Frais d\'installation',
    commissionMin: 'Commission (%) - Min. 20%',
    status: 'Statut',
    adminNotes: 'Notes Admin',
    internalNotes: 'Notes internes...',
    cancel: 'Annuler',
    save: 'Enregistrer',
    configure: 'Configurer',
    start: 'Démarrer',
    approve: 'Approuver',
    reject: 'Rejeter',
    offer: 'Offre',
    recentDemos: 'Démos Récentes',
    landingLinked: 'Landing Page Liée',
    visits: 'Visites',
    conversions: 'Conversions',
    appointments: 'Rendez-vous',
    view: 'Voir',
    pendingPayments: 'Paiements en Attente',
    markAsPaid: 'Marquer comme Payé',
    paymentReceived: 'Paiement Reçu',
    pendingCommissions: 'Commissions en Attente',
    collectCommission: 'Encaisser',
    totalPending: 'Total en Attente',
    setupPaid: 'Setup Payé',
    setupPending: 'Setup en Attente'
  }
};

const STATUS_CONFIG = {
  draft: { label: { es: 'Borrador', en: 'Draft', fr: 'Brouillon' }, color: 'bg-gray-100 text-gray-700', icon: Clock },
  pending_review: { label: { es: 'En Revisión', en: 'In Review', fr: 'En Révision' }, color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  approved: { label: { es: 'Aprobada', en: 'Approved', fr: 'Approuvée' }, color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  active: { label: { es: 'Activa', en: 'Active', fr: 'Active' }, color: 'bg-green-100 text-green-700', icon: Play },
  paused: { label: { es: 'Pausada', en: 'Paused', fr: 'En Pause' }, color: 'bg-orange-100 text-orange-700', icon: Pause },
  completed: { label: { es: 'Completada', en: 'Completed', fr: 'Terminée' }, color: 'bg-purple-100 text-purple-700', icon: CheckCircle },
  rejected: { label: { es: 'Rechazada', en: 'Rejected', fr: 'Rejetée' }, color: 'bg-red-100 text-red-700', icon: XCircle }
};

export const AdminReactivationSection = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('es') ? 'es' : i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const t = TEXTS[lang];
  
  const [campaigns, setCampaigns] = useState([]);
  const [demos, setDemos] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [commissions, setCommissions] = useState({ businesses: [], global_totals: {} });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('campaigns');
  const [globalMetrics, setGlobalMetrics] = useState({
    total_campaigns: 0,
    active_campaigns: 0,
    total_contacts: 0,
    total_converted: 0,
    total_revenue: 0,
    total_commission: 0
  });

  const fetchCampaigns = useCallback(async () => {
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      
      const response = await axios.get(`${API}/api/reactivation/admin/campaigns`, {
        headers: getAdminHeaders(),
        params
      });
      
      setCampaigns(response.data);
      
      // Calculate global metrics
      const metrics = response.data.reduce((acc, c) => ({
        total_campaigns: acc.total_campaigns + 1,
        active_campaigns: acc.active_campaigns + (c.status === 'active' ? 1 : 0),
        total_contacts: acc.total_contacts + (c.metrics?.total_contacts || 0),
        total_converted: acc.total_converted + (c.metrics?.converted || 0),
        total_revenue: acc.total_revenue + (c.metrics?.total_revenue || 0),
        total_commission: acc.total_commission + (c.metrics?.commission_earned || 0),
        landing_conversions: acc.landing_conversions + (c.metrics?.landing_conversions || 0),
        landing_appointments: acc.landing_appointments + (c.metrics?.landing_appointments || 0)
      }), {
        total_campaigns: 0,
        active_campaigns: 0,
        total_contacts: 0,
        total_converted: 0,
        total_revenue: 0,
        total_commission: 0,
        landing_conversions: 0,
        landing_appointments: 0
      });
      
      setGlobalMetrics(metrics);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Error al cargar campañas');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchDemos = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/reactivation/admin/demos`, {
        headers: getAdminHeaders()
      });
      setDemos(response.data);
    } catch (error) {
      console.error('Error fetching demos:', error);
    }
  }, []);

  const fetchPendingPayments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/reactivation/admin/campaigns/pending-payment`, {
        headers: getAdminHeaders()
      });
      setPendingPayments(response.data.campaigns || []);
    } catch (error) {
      console.error('Error fetching pending payments:', error);
    }
  }, []);

  const fetchCommissions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/reactivation/admin/commissions`, {
        headers: getAdminHeaders()
      });
      setCommissions(response.data);
    } catch (error) {
      console.error('Error fetching commissions:', error);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
    fetchDemos();
    fetchPendingPayments();
    fetchCommissions();
  }, [fetchCampaigns, fetchDemos, fetchPendingPayments, fetchCommissions]);

  const handleUpdateCampaign = async (campaignId, updates) => {
    try {
      await axios.put(`${API}/api/reactivation/admin/campaigns/${campaignId}`, updates, {
        headers: getAdminHeaders()
      });
      toast.success('Campaña actualizada');
      fetchCampaigns();
      setEditingCampaign(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleStartCampaign = async (campaignId) => {
    try {
      await axios.post(`${API}/api/reactivation/admin/campaigns/${campaignId}/start`, {}, {
        headers: getAdminHeaders()
      });
      toast.success('Campaña iniciada - enviando mensajes');
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar');
    }
  };

  const copyDemoLink = () => {
    const link = `${window.location.origin}/demo-reactivacion`;
    navigator.clipboard.writeText(link);
    toast.success(lang === 'es' ? 'Link copiado al portapapeles' : lang === 'fr' ? 'Lien copié' : 'Link copied to clipboard');
  };

  const handleMarkSetupPaid = async (campaignId) => {
    try {
      await axios.post(`${API}/api/reactivation/admin/campaigns/${campaignId}/mark-paid`, {}, {
        headers: getAdminHeaders()
      });
      toast.success(lang === 'es' ? 'Pago registrado' : lang === 'fr' ? 'Paiement enregistré' : 'Payment recorded');
      fetchCampaigns();
      fetchPendingPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleSetPricing = async (campaignId, setupFee, commissionPercentage) => {
    try {
      await axios.post(`${API}/api/reactivation/admin/campaigns/${campaignId}/set-pricing`, null, {
        headers: getAdminHeaders(),
        params: { setup_fee: setupFee, commission_percentage: commissionPercentage }
      });
      toast.success(lang === 'es' ? 'Precios actualizados' : lang === 'fr' ? 'Prix mis à jour' : 'Pricing updated');
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Zap className="w-6 h-6 text-amber-500" />
            {t.title}
          </h2>
          <p className="text-gray-600 text-sm mt-1">
            {t.subtitle}
          </p>
        </div>
        <Button onClick={copyDemoLink} variant="outline" className="gap-2">
          <Link2 className="w-4 h-4" />
          {t.copyDemoLink}
        </Button>
      </div>

      {/* Sub-tabs for Campaigns / Billing */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveSubTab('campaigns')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeSubTab === 'campaigns' 
              ? 'border-amber-500 text-amber-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Zap className="w-4 h-4 inline mr-2" />
          {t.campaigns}
        </button>
        <button
          onClick={() => setActiveSubTab('billing')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeSubTab === 'billing' 
              ? 'border-amber-500 text-amber-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Receipt className="w-4 h-4 inline mr-2" />
          {t.pendingPayments} ({pendingPayments.length})
        </button>
        <button
          onClick={() => setActiveSubTab('commissions')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeSubTab === 'commissions' 
              ? 'border-amber-500 text-amber-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <DollarSign className="w-4 h-4 inline mr-2" />
          {t.commission}
        </button>
      </div>

      {/* Global Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="text-sm text-gray-500 mb-1">{t.campaigns}</div>
          <div className="text-2xl font-bold text-gray-900">{globalMetrics.total_campaigns}</div>
        </div>
        <div className="bg-green-50 rounded-xl p-4 border border-green-200">
          <div className="text-sm text-green-600 mb-1">{t.active}</div>
          <div className="text-2xl font-bold text-green-700">{globalMetrics.active_campaigns}</div>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
          <div className="text-sm text-blue-600 mb-1">{t.contacts}</div>
          <div className="text-2xl font-bold text-blue-700">{globalMetrics.total_contacts.toLocaleString()}</div>
        </div>
        <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
          <div className="text-sm text-purple-600 mb-1">{t.converted}</div>
          <div className="text-2xl font-bold text-purple-700">{globalMetrics.total_converted}</div>
        </div>
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
          <div className="text-sm text-amber-600 mb-1">{t.revenue}</div>
          <div className="text-2xl font-bold text-amber-700">€{globalMetrics.total_revenue.toLocaleString()}</div>
        </div>
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
          <div className="text-sm text-emerald-600 mb-1">{t.commission}</div>
          <div className="text-2xl font-bold text-emerald-700">€{globalMetrics.total_commission.toLocaleString()}</div>
        </div>
      </div>

      {/* BILLING TAB */}
      {activeSubTab === 'billing' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-amber-500" />
            {t.pendingPayments}
          </h3>
          
          {pendingPayments.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center border">
              <CheckCircle className="w-12 h-12 mx-auto text-green-400 mb-3" />
              <p className="text-gray-500">{lang === 'es' ? 'No hay pagos pendientes' : lang === 'fr' ? 'Aucun paiement en attente' : 'No pending payments'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingPayments.map((campaign) => (
                <div key={campaign.id} className="bg-white rounded-xl p-4 border flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900">{campaign.name}</h4>
                    <p className="text-sm text-gray-500">
                      {campaign.business_name} • {campaign.contacts_count} {t.contacts}
                    </p>
                    <p className="text-xs text-gray-400">{campaign.business_email}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-amber-600">€{campaign.setup_fee}</p>
                      <p className="text-xs text-gray-500">{t.setupFee}</p>
                    </div>
                    <Button
                      onClick={() => handleMarkSetupPaid(campaign.id)}
                      className="bg-green-500 hover:bg-green-600"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      {t.markAsPaid}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* COMMISSIONS TAB */}
      {activeSubTab === 'commissions' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-500" />
              {t.pendingCommissions}
            </h3>
            <div className="bg-emerald-100 text-emerald-700 px-4 py-2 rounded-lg">
              <span className="text-sm">{t.totalPending}:</span>
              <span className="text-xl font-bold ml-2">€{commissions.global_totals?.pending_commission?.toFixed(2) || 0}</span>
            </div>
          </div>
          
          {commissions.businesses?.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center border">
              <DollarSign className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">{lang === 'es' ? 'No hay comisiones registradas' : lang === 'fr' ? 'Aucune commission enregistrée' : 'No commissions recorded'}</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{t.business}</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">{lang === 'es' ? 'Ventas' : lang === 'fr' ? 'Ventes' : 'Sales'}</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">{t.commission}</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">{lang === 'es' ? 'Pendiente' : lang === 'fr' ? 'En attente' : 'Pending'}</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">{lang === 'es' ? 'Cobrado' : lang === 'fr' ? 'Encaissé' : 'Collected'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {commissions.businesses?.map((biz) => (
                    <tr key={biz.business_id}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{biz.business_name}</div>
                        <div className="text-xs text-gray-500">{biz.sales_count} {lang === 'es' ? 'ventas' : lang === 'fr' ? 'ventes' : 'sales'}</div>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">€{biz.total_sales?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-medium text-emerald-600">€{biz.total_commission?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-amber-600">€{biz.pending_commission?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-green-600">€{biz.collected_commission?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 font-semibold">
                  <tr>
                    <td className="px-4 py-3">TOTAL</td>
                    <td className="px-4 py-3 text-right">€{commissions.global_totals?.total_sales?.toFixed(2) || 0}</td>
                    <td className="px-4 py-3 text-right text-emerald-600">€{commissions.global_totals?.total_commission?.toFixed(2) || 0}</td>
                    <td className="px-4 py-3 text-right text-amber-600">€{commissions.global_totals?.pending_commission?.toFixed(2) || 0}</td>
                    <td className="px-4 py-3 text-right text-green-600">€{commissions.global_totals?.collected_commission?.toFixed(2) || 0}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* CAMPAIGNS TAB */}
      {activeSubTab === 'campaigns' && (
        <>
          {/* Demo Link Section */}
          <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold mb-1">{t.demoLinkTitle}</h3>
                <p className="text-amber-100 text-sm mb-2">
                  {t.demoLinkDesc}
                </p>
                <code className="bg-black/20 px-3 py-1 rounded text-sm">
                  {window.location.origin}/demo-reactivacion
                </code>
              </div>
              <Button 
                onClick={copyDemoLink}
                className="bg-white text-amber-600 hover:bg-amber-50"
              >
                {t.copyDemoLink}
              </Button>
            </div>
          </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="flex-1">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full md:w-auto px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500"
          >
            <option value="">{t.allStatuses}</option>
            <option value="pending_review">{t.inReview}</option>
            <option value="approved">{t.approved}</option>
            <option value="active">{t.active}</option>
            <option value="completed">{t.completed}</option>
            <option value="rejected">{t.rejected}</option>
          </select>
        </div>
        <Button onClick={() => { fetchCampaigns(); fetchPendingPayments(); fetchCommissions(); }} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Campaigns List */}
      {campaigns.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center">
          <Zap className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t.noCampaigns} {statusFilter && `(${STATUS_CONFIG[statusFilter]?.label[lang]})`}
          </h3>
          <p className="text-gray-500">
            {t.campaignsWillAppear}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {campaigns.map((campaign) => {
            const statusConfig = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.draft;
            const StatusIcon = statusConfig.icon;
            
            return (
              <div
                key={campaign._id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-gray-900">{campaign.name}</h3>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${statusConfig.color}`}>
                          <StatusIcon className="w-4 h-4" />
                          {statusConfig.label[lang]}
                        </span>
                        {/* Setup Fee Badge */}
                        {campaign.setup_fee > 0 && (
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            campaign.setup_fee_paid 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {campaign.setup_fee_paid ? t.setupPaid : t.setupPending}: €{campaign.setup_fee}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {t.business}: <strong>{campaign.business_name}</strong> | 
                        {t.created}: {new Date(campaign.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {/* Metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <Users className="w-4 h-4 mx-auto text-gray-400 mb-1" />
                      <p className="text-lg font-bold text-gray-900">{campaign.metrics?.total_contacts || 0}</p>
                      <p className="text-xs text-gray-500">{t.contacts}</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <MessageSquare className="w-4 h-4 mx-auto text-blue-500 mb-1" />
                      <p className="text-lg font-bold text-blue-600">{campaign.metrics?.responded || 0}</p>
                      <p className="text-xs text-gray-500">{t.responses}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <CheckCircle className="w-4 h-4 mx-auto text-green-500 mb-1" />
                      <p className="text-lg font-bold text-green-600">{campaign.metrics?.converted || 0}</p>
                      <p className="text-xs text-gray-500">{t.converted}</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3 text-center">
                      <DollarSign className="w-4 h-4 mx-auto text-amber-500 mb-1" />
                      <p className="text-lg font-bold text-amber-600">€{campaign.metrics?.total_revenue || 0}</p>
                      <p className="text-xs text-gray-500">{t.revenue}</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                      <Percent className="w-4 h-4 mx-auto text-purple-500 mb-1" />
                      <p className="text-lg font-bold text-purple-600">{campaign.commission_percentage || 30}%</p>
                      <p className="text-xs text-gray-500">{t.commission}</p>
                    </div>
                  </div>

                  {/* Landing Info (si usa landing) */}
                  {campaign.use_landing && campaign.landing_url && (
                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 mb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                            <Link2 className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-emerald-800">{t.landingLinked}</p>
                            <p className="text-sm text-emerald-600">{campaign.landing_url}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <div className="text-center">
                            <p className="font-bold text-emerald-700">{campaign.metrics?.landing_views || 0}</p>
                            <p className="text-xs text-emerald-600">{t.visits}</p>
                          </div>
                          <div className="text-center">
                            <p className="font-bold text-emerald-700">{campaign.metrics?.landing_conversions || 0}</p>
                            <p className="text-xs text-emerald-600">{t.conversions}</p>
                          </div>
                          <div className="text-center">
                            <p className="font-bold text-emerald-700">{campaign.metrics?.landing_appointments || 0}</p>
                            <p className="text-xs text-emerald-600">{t.appointments}</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(campaign.landing_url, '_blank')}
                            className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            {t.view}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Edit Form */}
                  {editingCampaign === campaign._id && (
                    <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-4">
                      <h4 className="font-semibold text-gray-900">{t.configure}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1 block">
                            {t.setupFee} (€)
                          </label>
                          <Input
                            type="number"
                            defaultValue={campaign.setup_fee}
                            id={`setup-${campaign._id}`}
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1 block">
                            {t.commissionMin}
                          </label>
                          <Input
                            type="number"
                            defaultValue={campaign.commission_percentage || 30}
                            min="20"
                            id={`commission-${campaign._id}`}
                            placeholder="30"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1 block">
                            {t.status}
                          </label>
                          <select
                            defaultValue={campaign.status}
                            id={`status-${campaign._id}`}
                            className="w-full px-3 py-2 rounded-lg border border-gray-300"
                          >
                            <option value="pending_review">{t.inReview}</option>
                            <option value="approved">{t.approved}</option>
                            <option value="rejected">{t.rejected}</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">
                          {t.adminNotes}
                        </label>
                        <Input
                          defaultValue={campaign.admin_notes || ''}
                          id={`notes-${campaign._id}`}
                          placeholder={t.internalNotes}
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setEditingCampaign(null)}
                        >
                          {t.cancel}
                        </Button>
                        <Button
                          className="bg-amber-500 hover:bg-amber-600"
                          onClick={() => {
                            const setupFee = parseFloat(document.getElementById(`setup-${campaign._id}`).value) || 0;
                            const commission = parseFloat(document.getElementById(`commission-${campaign._id}`).value) || 30;
                            const status = document.getElementById(`status-${campaign._id}`).value;
                            const notes = document.getElementById(`notes-${campaign._id}`).value;
                            
                            handleUpdateCampaign(campaign._id, {
                              setup_fee: setupFee,
                              commission_percentage: Math.max(20, commission),
                              status: status,
                              admin_notes: notes
                            });
                          }}
                        >
                          {t.save}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                    <div className="text-sm text-gray-500">
                      {campaign.setup_fee > 0 && (
                        <span className="mr-4">{t.setupFee}: €{campaign.setup_fee}</span>
                      )}
                      {t.offer}: {campaign.target_offer}
                    </div>
                    <div className="flex gap-2">
                      {/* Mark as Paid button if setup fee pending */}
                      {campaign.setup_fee > 0 && !campaign.setup_fee_paid && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-green-300 text-green-600 hover:bg-green-50"
                          onClick={() => handleMarkSetupPaid(campaign._id)}
                        >
                          <CreditCard className="w-4 h-4 mr-1" />
                          {t.markAsPaid}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingCampaign(editingCampaign === campaign._id ? null : campaign._id)}
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        {t.configure}
                      </Button>
                      {campaign.status === 'approved' && (
                        <Button
                          size="sm"
                          className="bg-green-500 hover:bg-green-600"
                          onClick={() => handleStartCampaign(campaign._id)}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          {t.start}
                        </Button>
                      )}
                      {campaign.status === 'pending_review' && (
                        <>
                          <Button
                            size="sm"
                            className="bg-green-500 hover:bg-green-600"
                            onClick={() => handleUpdateCampaign(campaign._id, { status: 'approved' })}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            {t.approve}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleUpdateCampaign(campaign._id, { status: 'rejected' })}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            {t.reject}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Recent Demos Section */}
      {demos.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-amber-500" />
            {t.recentDemos}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {demos.slice(0, 6).map((demo) => (
              <div key={demo._id} className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{demo.business_name}</h4>
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    demo.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {demo.status === 'active' ? (lang === 'es' ? 'Activa' : lang === 'fr' ? 'Active' : 'Active') : (lang === 'es' ? 'Completada' : lang === 'fr' ? 'Terminée' : 'Completed')}
                  </span>
                </div>
                <p className="text-sm text-gray-500">{demo.business_type}</p>
                <p className="text-sm text-gray-500">{demo.owner_name} - {demo.owner_phone}</p>
                <p className="text-xs text-gray-400 mt-2">
                  {new Date(demo.created_at).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default AdminReactivationSection;
